import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useConfig } from "../lib/useConfig";
import { useAuth } from "../context/AuthContext";
import GlobalFilters from "../components/GlobalFilters";
import { useLang } from "../lib/i18n";
import { PageHeader, StatusBadge } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Download, Eye, CheckCircle2, Flag, Pencil, Search, FileSpreadsheet } from "lucide-react";

const act_short = (arr, map) => arr?.slice(0, 2).map((a) => map[a] || a).join(", ") + (arr?.length > 2 ? ` +${arr.length - 2}` : "");

export default function DailyReports() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: cfg } = useConfig();
  const { t } = useLang();
  const actMap = useMemo(() => Object.fromEntries((cfg?.activities || []).map((a) => [a.code, a.name])), [cfg]);
  const findMap = useMemo(() => Object.fromEntries((cfg?.finding_categories || []).map((a) => [a.code, a.name])), [cfg]);
  const chMap = useMemo(() => Object.fromEntries((cfg?.challenge_categories || []).map((a) => [a.code, a.name])), [cfg]);

  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [anon, setAnon] = useState(false);

  const params = { ...filters, page, limit: 15 };
  const { data, isLoading } = useQuery({
    queryKey: ["reports", params],
    queryFn: async () => (await api.get("/reports", { params })).data,
  });

  const canValidate = ["provincial_supervisor", "admin"].includes(user?.role);

  const act = async (fn, msg) => { await fn(); toast.success(msg); qc.invalidateQueries({ queryKey: ["reports"] }); if (detail) setDetail(null); };

  const exportData = (fmt) => {
    const token = localStorage.getItem("token");
    const qs = new URLSearchParams({ ...filters, fmt, anonymize: anon ? "true" : "false" }).toString();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/dataset?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = `surveillance_dataset.${fmt === "xlsx" ? "xlsx" : "csv"}`; a.click();
        URL.revokeObjectURL(url);
        toast.success(t("Export démarré."));
      });
  };

  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;

  return (
    <div data-testid="reports-page">
      <PageHeader title={t("Base Centrale des Rapports Quotidiens")} subtitle={`${data?.total ?? 0} ${t("rapport(s)")}`}>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer mr-1" title={t("Remplace les noms des agents par leur matricule dans l'export")}>
          <input type="checkbox" className="h-4 w-4 accent-[#F05A28]" checked={anon} onChange={(e) => setAnon(e.target.checked)} data-testid="export-anonymize" />
          {t("Anonymiser (matricule)")}
        </label>
        <Button variant="outline" size="sm" onClick={() => exportData("csv")} data-testid="export-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
        <Button variant="outline" size="sm" onClick={() => exportData("xlsx")} data-testid="export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
        <Button size="sm" onClick={() => navigate("/new-report")} className="bg-[#F05A28] hover:bg-[#D84A1B]">+ {t("Nouveau")}</Button>
      </PageHeader>

      <GlobalFilters value={filters} onChange={(f) => { setFilters(f); setPage(1); }} />

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>{t("Date")}</TableHead><TableHead>{t("Épidémiologiste")}</TableHead>
                <TableHead>{t("Zone de Santé")}</TableHead><TableHead>{t("Activités")}</TableHead>
                <TableHead className="text-right">{t("Alertes")}</TableHead>
                <TableHead className="text-right">{t("Invest.")}</TableHead>
                <TableHead className="text-right">{t("Cas susp.")}</TableHead>
                <TableHead className="text-right">Contacts</TableHead>
                <TableHead>{t("Statut")}</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-8">Chargement…</TableCell></TableRow>
              ) : data?.items?.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-8">Aucun rapport.</TableCell></TableRow>
              ) : data?.items?.map((r) => (
                <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`report-row-${r.id}`}>
                  <TableCell className="font-mono text-xs">{r.report_date}<span className="text-slate-400"> · S{r.epi_week}</span></TableCell>
                  <TableCell className="text-sm">{r.epidemiologist_name}</TableCell>
                  <TableCell className="text-sm">{r.health_zone}<div className="text-xs text-slate-500">{r.province}</div></TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[180px]">{act_short(r.activities, actMap)}</TableCell>
                  <TableCell className="text-right text-sm">{r.metrics?.alerts_received}</TableCell>
                  <TableCell className="text-right text-sm">{r.metrics?.alerts_investigated}</TableCell>
                  <TableCell className="text-right text-sm">{r.metrics?.suspected_cases}</TableCell>
                  <TableCell className="text-right text-sm">{r.metrics?.contacts_followed}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => setDetail(r)} data-testid={`view-${r.id}`}><Eye className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="flex items-center justify-between mt-4">
        <p className="text-sm text-slate-500">Page {page} / {totalPages || 1}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page">{t("Précédent")}</Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="next-page">{t("Suivant")}</Button>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{t("Rapport du")} {detail.report_date} <StatusBadge status={detail.status} /></DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <Info label={t("Épidémiologiste")} value={detail.epidemiologist_name} />
                  <Info label={t("Zone de Santé")} value={detail.health_zone} />
                  <Info label={t("Province / Territoire")} value={`${detail.province} · ${detail.territory}`} />
                  <Info label={t("Aire de Santé")} value={detail.health_area || "-"} />
                  <Info label={t("Score qualité")} value={`${detail.quality_score}/100`} />
                  <Info label={t("Semaine épi.")} value={`S${detail.epi_week}/${detail.epi_year}`} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-400 mb-1">{t("Activités")}</p>
                  <div className="flex flex-wrap gap-1.5">{detail.activities?.map((a) => <span key={a} className="text-xs bg-[#FFF3EE] text-[#F05A28] px-2 py-0.5 rounded">{actMap[a] || a}</span>)}</div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-400 mb-1">{t("Chiffres")}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(detail.metrics || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                      <div key={k} className="bg-slate-50 rounded-lg px-2 py-1.5"><p className="text-[10px] text-slate-400 leading-tight">{k}</p><p className="font-semibold">{v}</p></div>
                    ))}
                  </div>
                </div>
                {detail.findings?.length > 0 && <div><p className="text-xs font-semibold uppercase text-slate-400 mb-1">{t("Constats")}</p>{detail.findings.map((f, i) => <p key={i} className="text-slate-600">• {findMap[f.category] || f.category}{f.description ? ` · ${f.description}` : ""}</p>)}</div>}
                {detail.challenges?.length > 0 && <div><p className="text-xs font-semibold uppercase text-slate-400 mb-1">{t("Défis")}</p>{detail.challenges.map((c, i) => <p key={i} className="text-slate-600">• {chMap[c.category] || c.category} <span className="text-slate-400">({t(c.severity)})</span></p>)}</div>}
                {detail.success_story && <div><p className="text-xs font-semibold uppercase text-slate-400 mb-1">Succès / Impact</p><p className="text-slate-600">{detail.success_story}</p></div>}
                {detail.quality_flags?.length > 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-3"><p className="text-xs font-semibold text-red-700 mb-1">Anomalies de cohérence</p>{detail.quality_flags.map((f, i) => <p key={i} className="text-red-600 text-xs">• {f}</p>)}</div>}
              </div>
              <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-slate-100 mt-2">
                {(user.role === "field_epi" ? detail.epidemiologist_id === user.id : true) && (
                  <Button variant="outline" size="sm" onClick={() => navigate(`/new-report?edit=${detail.id}`)} data-testid="edit-report"><Pencil className="h-4 w-4 mr-1" /> {t("Éditer")}</Button>
                )}
                {canValidate && detail.status !== "validated" && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="validate-report" onClick={() => act(() => api.post(`/reports/${detail.id}/validate`), t("Rapport validé"))}><CheckCircle2 className="h-4 w-4 mr-1" /> {t("Valider")}</Button>
                )}
                {canValidate && (
                  <Button variant="outline" size="sm" className="text-red-600" data-testid="flag-report" onClick={() => act(() => api.post(`/reports/${detail.id}/flag`), t("Rapport signalé"))}><Flag className="h-4 w-4 mr-1" /> {t("Signaler")}</Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div><p className="text-xs text-slate-500">{label}</p><p className="font-medium text-slate-700">{value}</p></div>
);
